import React from 'react';
import ArticleLayout from '../../components/articles/ArticleLayout';

const DesigningAISystemsArticle = () => (
  <ArticleLayout articleId="designing-ai-systems-business-problems">
    <section>
      <h2>Designing AI Systems That Solve Business Problems (Not Just Impress)</h2>
      <p>
        AI projects fail less often because the model is weak and more often because the <strong>system around it is
        missing.</strong> A model can be accurate in a notebook and still useless in production if it cannot access the
        right data, explain its outputs, fit into workflows, or stay reliable over time. This article lays out how to
        design AI systems that create measurable business outcomes, from problem definition through long-term operations.
      </p>
    </section>

    <hr />

    <section>
      <h3>Start With the Business Decision, Not the Model</h3>
      <p>An AI system should clearly support one of the following:</p>
      <ul>
        <li>A faster decision</li>
        <li>A better decision</li>
        <li>A cheaper decision</li>
        <li>A decision that was not feasible before</li>
      </ul>
      <p>If you cannot name the decision and who makes it, you are not designing an AI system yet.</p>

      <h4>Good starting points</h4>
      <ul>
        <li>“Route support tickets to the correct team within 60 seconds”</li>
        <li>“Flag potentially fraudulent orders before fulfillment”</li>
        <li>“Forecast inventory needs weekly with defined confidence bounds”</li>
        <li>“Reduce manual document review time while preserving compliance”</li>
      </ul>

      <h4>Weak starting points</h4>
      <ul>
        <li>“Add AI to the product”</li>
        <li>“Build a chatbot”</li>
        <li>“Use machine learning to optimize operations”</li>
      </ul>
    </section>

    <section>
      <h3>AI Feature vs AI System</h3>
      <p>A feature is the visible part a user interacts with. A system includes the invisible components that make the feature reliable.</p>

      <h4>An AI system typically includes</h4>
      <ul>
        <li>Data ingestion and validation</li>
        <li>Feature generation or retrieval</li>
        <li>Model inference service</li>
        <li>Business rules and fallback logic</li>
        <li>Human review workflow when needed</li>
        <li>Logging, monitoring, and alerting</li>
        <li>Evaluation pipeline and retraining triggers</li>
        <li>Governance, access control, and audit trail</li>
      </ul>
      <blockquote>
        <p>“A model answers a question. A system delivers a decision safely, consistently, and on time.”</p>
      </blockquote>
    </section>

    <section>
      <h3>Use Case Selection That Survives Contact With Reality</h3>
      <p>Choose use cases using a simple filter: <strong>value, feasibility, and risk.</strong></p>

      <h4>Value questions</h4>
      <ul>
        <li>What does success change in dollars, time, or risk exposure?</li>
        <li>Who benefits and how often?</li>
        <li>What metric will move if this works?</li>
      </ul>

      <h4>Feasibility questions</h4>
      <ul>
        <li>Do we have historical data that matches the decision?</li>
        <li>Are labels available, or can we create them?</li>
        <li>Can we integrate into the workflow without heavy change management?</li>
      </ul>

      <h4>Risk questions</h4>
      <ul>
        <li>What happens when the system is wrong?</li>
        <li>Are there regulatory or privacy constraints?</li>
        <li>Do we need explainability for users, auditors, or customers?</li>
      </ul>

      <p><strong>Prioritization rule:</strong> start with medium risk, high repetition, and clear outcomes.</p>
    </section>

    <section>
      <h3>Data Readiness Is Usually the Real Bottleneck</h3>
      <p>Most AI delivery timelines are dominated by data issues, not training.</p>

      <h4>Common data blockers</h4>
      <ul>
        <li>Data exists but is not accessible across teams or tools</li>
        <li>The same concept is defined differently in different systems</li>
        <li>Missing fields, inconsistent formats, and poor timestamps</li>
        <li>Labels do not exist or do not match operational reality</li>
        <li>Data is too sparse for the decision frequency you need</li>
      </ul>

      <h4>What “ready” looks like</h4>
      <ul>
        <li>Clear ownership of each data source</li>
        <li>Stable schemas with validation</li>
        <li>Definitions documented for key fields</li>
        <li>A repeatable pipeline to produce training and inference data</li>
        <li>Privacy constraints understood and enforced</li>
      </ul>
      <p><strong>Tip:</strong> design the data pipeline as a product. If it is brittle, your AI system will be brittle.</p>
    </section>

    <section>
      <h3>Define Success Metrics That Match the Business</h3>
      <p>Model accuracy is rarely the metric leadership cares about. Translate model performance into business impact.</p>

      <h4>Model level metrics</h4>
      <ul>
        <li>Precision, recall, F1</li>
        <li>ROC AUC or PR AUC for imbalance</li>
        <li>Calibration, especially for risk scoring</li>
        <li>Latency and throughput</li>
      </ul>

      <h4>Business level metrics</h4>
      <ul>
        <li>Cycle time reduction</li>
        <li>Cost per case</li>
        <li>Revenue lift or churn reduction</li>
        <li>Fraud losses prevented</li>
        <li>SLA compliance improvement</li>
        <li>Human review workload change</li>
      </ul>

      <h4>Decision quality metrics</h4>
      <ul>
        <li>Error cost by type</li>
        <li>False positive vs false negative cost</li>
        <li>Percentage routed to human review</li>
        <li>Customer impact metrics when applicable</li>
      </ul>
      <p>A strong system explicitly defines the cost of being wrong.</p>
    </section>

    <section>
      <h3>Model Choice Is Secondary to System Design</h3>
      <p>Failure often stems from debating model architectures before defining constraints.</p>

      <h4>Constraints that should drive model choice</h4>
      <ul>
        <li>Required latency and concurrency</li>
        <li>Data availability and quality</li>
        <li>Explainability needs</li>
        <li>Update frequency and model drift risk</li>
        <li>Operating cost tolerance</li>
        <li>Integration environment and security requirements</li>
      </ul>

      <h4>Simple decision guide</h4>
      <ul>
        <li>If rules are stable and explainability is critical, start with rules plus basic ML.</li>
        <li>If classification is complex and data is rich, use supervised learning with clear evaluation.</li>
        <li>If you need natural language outputs, use LLMs with retrieval and guardrails.</li>
        <li>If correctness must be very high, include human review and conservative thresholds.</li>
      </ul>
    </section>

    <section>
      <h3>System Architecture Patterns That Work</h3>

      <h4>Pattern 1: Predict then decide</h4>
      <ul>
        <li>Model produces a score or label.</li>
        <li>Business logic converts that into an action.</li>
        <li>Thresholds can be tuned without retraining the model.</li>
      </ul>
      <p><strong>Best for:</strong> risk scoring, routing, prioritization.</p>

      <h4>Pattern 2: Assistive AI with human in the loop</h4>
      <ul>
        <li>AI drafts, summarizes, or extracts.</li>
        <li>Human approves, edits, or rejects.</li>
        <li>Feedback becomes training data.</li>
      </ul>
      <p><strong>Best for:</strong> compliance workflows, support, operations.</p>

      <h4>Pattern 3: Retrieval augmented decision support</h4>
      <ul>
        <li>Retrieve relevant internal knowledge.</li>
        <li>Generate an answer grounded in sources.</li>
        <li>Log references and confidence indicators.</li>
      </ul>
      <p><strong>Best for:</strong> internal copilots, knowledge bases, policy guidance.</p>

      <h4>Pattern 4: Automation with safe fallback</h4>
      <ul>
        <li>AI handles the standard path.</li>
        <li>Exceptions go to deterministic fallback or human review.</li>
        <li>Clear escalation rules prevent silent failures.</li>
      </ul>
      <p><strong>Best for:</strong> document processing, intake triage, content moderation.</p>
    </section>

    <section>
      <h3>Guardrails: Make Failure Safe and Visible</h3>
      <p>AI systems will fail sometimes. Your job is to make failures controlled, detected, and recoverable.</p>

      <h4>Guardrails to implement early</h4>
      <ul>
        <li>Confidence thresholds and abstain behavior</li>
        <li>Fallback path when confidence is low</li>
        <li>Rate limits and abuse detection for public endpoints</li>
        <li>Input validation and prompt injection defenses for LLM use cases</li>
        <li>Output constraints (e.g., structured JSON with schema validation)</li>
        <li>Redaction of sensitive data in logs</li>
        <li>Audit trail for regulated workflows</li>
      </ul>
      <p><strong>Operational rule:</strong> if a wrong output can cause serious harm, the system must be able to say “I do not know” and route to a human.</p>
    </section>

    <section>
      <h3>Deployment and Operations: The Part Many Teams Skip</h3>

      <h4>Production requirements checklist</h4>
      <ul>
        <li>Monitoring for data drift and concept drift</li>
        <li>Alerting on latency, error rate, and abnormal predictions</li>
        <li>Canary releases and rollback strategy</li>
        <li>Versioning for model, data, and prompts</li>
        <li>Reproducible training runs</li>
        <li>Access control for training data and inference endpoints</li>
      </ul>

      <h4>Retraining is not always the answer</h4>
      <p>Sometimes the best fix is:</p>
      <ul>
        <li>Better data validation</li>
        <li>Better thresholds</li>
        <li>Better user experience and feedback loops</li>
        <li>Better routing to human review</li>
      </ul>
    </section>

    <section>
      <h3>Common Failure Modes and How to Avoid Them</h3>
      <h4>Mistake 1: Building a demo instead of a decision system</h4>
      <p><strong>Fix:</strong> define the end-to-end workflow integration before training.</p>

      <h4>Mistake 2: No plan for edge cases</h4>
      <p><strong>Fix:</strong> design exception flows, not just the happy path.</p>

      <h4>Mistake 3: Measuring the wrong metric</h4>
      <p><strong>Fix:</strong> tie model metrics to cost of error and business KPIs.</p>

      <h4>Mistake 4: Ignoring change management</h4>
      <p><strong>Fix:</strong> train users, update SOPs, and make ownership explicit.</p>

      <h4>Mistake 5: No monitoring, no governance</h4>
      <p><strong>Fix:</strong> treat AI like a living component, not a one-time launch.</p>
    </section>

    <section>
      <h3>Fun Fact That Helps Teams Align</h3>
      <p>
        In many production settings, the best ROI comes from systems that do not replace humans. They reduce “blank screen time”
        by pre-filling context, summarizing cases, and routing work correctly. The value is in improving throughput and
        consistency, not in full autonomy.
      </p>
    </section>

    <section>
      <h3>A Practical One Page AI System Spec Template</h3>
      <p>Use this structure to align stakeholders before building:</p>
      <ul>
        <li><strong>Decision:</strong> what decision is being supported?</li>
        <li><strong>Users:</strong> who uses it and where in the workflow?</li>
        <li><strong>Action:</strong> what happens after the output?</li>
        <li><strong>Success metrics:</strong> business and model level</li>
        <li><strong>Constraints:</strong> latency, cost, explainability, compliance</li>
        <li><strong>Data sources:</strong> owners, refresh frequency, key fields</li>
        <li><strong>Risk:</strong> error costs, harm scenarios, mitigation plan</li>
        <li><strong>Fallback:</strong> what happens when confidence is low or systems fail?</li>
        <li><strong>Monitoring:</strong> what gets tracked and who responds?</li>
        <li><strong>Iteration:</strong> feedback loop and review cadence</li>
      </ul>
    </section>

    <section>
      <h3>Closing Note</h3>
      <p>
        Designing AI systems is primarily systems engineering and operational design. The model is only one component.
        If the workflow, data, monitoring, and guardrails are solid, you can deliver meaningful results with simpler
        models. If those pieces are missing, even the best model will struggle to produce reliable business value.
      </p>
    </section>
  </ArticleLayout>
);

export default DesigningAISystemsArticle;
